import { supabase } from '@/integrations/backend/client';
import type { Link } from '@/types/editor';

export interface ABVariant {
  id: string;
  variant_name: string;
  title: string;
  url: string;
  icon: string | null;
  button_color: string | null;
  button_text_color: string | null;
  traffic_percentage: number;
}

interface ABVariantWithLink {
  variant: ABVariant | null;
  originalLink: Link;
}

/**
 * Gets or creates a session ID for tracking A/B test consistency
 */
export function getSessionId(): string {
  // Check if running in browser
  if (typeof window === 'undefined' || typeof sessionStorage === 'undefined') {
    return 'server-session';
  }
  
  const SESSION_KEY = 'qlivar_ab_session';
  let sessionId = sessionStorage.getItem(SESSION_KEY);
  
  if (!sessionId) {
    // Fallback for browsers that don't support crypto.randomUUID
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      sessionId = crypto.randomUUID();
    } else {
      // Simple UUID v4 fallback
      sessionId = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
        const r = Math.random() * 16 | 0;
        const v = c === 'x' ? r : (r & 0x3 | 0x8);
        return v.toString(16);
      });
    }
    
    try {
      sessionStorage.setItem(SESSION_KEY, sessionId);
    } catch (e) {
      // SessionStorage might be disabled
      console.warn('Could not save session ID to sessionStorage:', e);
    }
  }
  
  return sessionId;
}

/**
 * Selects an A/B test variant for a link based on traffic percentages
 * Uses consistent hashing to ensure same user gets same variant
 */
export async function selectABVariant(
  linkId: string,
  sessionId: string
): Promise<ABVariant | null> {
  try {
    // Fetch active variants for this link
    const { data: variants, error } = await supabase
      .from('ab_test_variants')
      .select('*')
      .eq('link_id', linkId)
      .eq('is_active', true)
      .order('traffic_percentage', { ascending: false });

    if (error || !variants || variants.length === 0) {
      return null;
    }

    // Create a deterministic hash from sessionId + linkId
    const hashInput = `${sessionId}-${linkId}`;
    let hash = 0;
    for (let i = 0; i < hashInput.length; i++) {
      const char = hashInput.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32bit integer
    }
    
    // Convert hash to percentage (0-100)
    const percentage = Math.abs(hash % 100);
    
    // Select variant based on traffic percentage
    let cumulativePercentage = 0;
    for (const variant of variants) {
      cumulativePercentage += variant.traffic_percentage;
      if (percentage < cumulativePercentage) {
        return variant as ABVariant;
      }
    }
    
    // If no variant selected (traffic doesn't add to 100%), return null (show original)
    return null;
  } catch (error) {
    console.error('Error selecting AB variant:', error);
    return null;
  }
}

/**
 * Tracks an A/B test impression (view)
 */
export async function trackABImpression(
  linkId: string,
  variantId: string | null,
  userId: string,
  sessionId: string
): Promise<void> {
  try {
    if (variantId) {
      await supabase.from('ab_test_views').insert({
        link_id: linkId,
        variant_id: variantId,
        user_id: userId,
        session_id: sessionId,
        referrer: document.referrer || null,
        user_agent: navigator.userAgent || null,
      });
    }
  } catch (error) {
    console.error('Error tracking AB impression:', error);
  }
}

/**
 * Processes links with A/B testing enabled and returns modified links
 */
export async function processLinksWithABTesting(
  links: Link[],
  userId: string
): Promise<Link[]> {
  // Check if running in browser
  if (typeof window === 'undefined') {
    return links; // Return original links during SSR
  }
  
  const sessionId = getSessionId();
  
  // Process all links in parallel
  const processedLinksPromises = links.map(async (link) => {
    if (!link.ab_testing_enabled) {
      return link;
    }
    
    try {
      // Select variant for this link
      const variant = await selectABVariant(link.id, sessionId);
      
      if (variant) {
        // Track impression (fire and forget)
        trackABImpression(link.id, variant.id, userId, sessionId).catch(err => {
          console.warn('Failed to track AB impression:', err);
        });
        
        // Return link with variant data
        return {
          ...link,
          title: variant.title,
          url: variant.url,
          icon: variant.icon || link.icon,
          button_color: variant.button_color || link.button_color,
          button_text_color: variant.button_text_color || link.button_text_color,
          _variant_id: variant.id, // Store for click tracking
          _is_variant: true,
        };
      }
    } catch (error) {
      console.error('Error processing AB test for link:', link.id, error);
    }
    
    // Fallback to original link if variant selection fails or no variant
    return link;
  });

  return Promise.all(processedLinksPromises);
}
